import { useCallback } from 'react';
import { emitTelemetry } from '@/lib/telemetry';
import type { GlobalSuggestKind } from '@/gql/graphql';

export interface SearchClickPayload {
  kind: GlobalSuggestKind;
  /** 1-indexed position in the flat ranked list (A60.12, §17.13). */
  rank: number;
  query: string;
}

export interface SearchDismissedPayload {
  query: string;
  totalHitsShown: number;
}

/**
 * Returns stable callbacks for emitting Global Search telemetry events.
 *
 * - `emitClick`:     fires `global_search.click` when the user selects a hit.
 * - `emitDismissed`: fires `global_search.dismissed` when the palette is closed
 *                    without selecting a hit (Esc / click-outside).
 */
export function useGlobalSearchTelemetry() {
  const emitClick = useCallback((payload: SearchClickPayload) => {
    const { kind, rank, query } = payload;
    emitTelemetry('global_search.click', { kind, rank, query });
  }, []);

  const emitDismissed = useCallback((payload: SearchDismissedPayload) => {
    const { query, totalHitsShown } = payload;
    emitTelemetry('global_search.dismissed', { query, totalHitsShown });
  }, []);

  return { emitClick, emitDismissed };
}
